import { useState } from 'react'
import { Milestone, AlertTriangle, Presentation } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { ListSkeleton } from '@/components/ui/skeleton'
import { useTrackerTickets } from '@/queries/tracker'
import { useTicketCompanies } from '@/queries/tickets'
import { useIsAdmin } from '@/lib/auth'
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner'
import {
  trackerStatusLabels,
  trackerStatusVariants,
  devStageLabels,
  devStageVariants,
  priorityLabels,
} from '@/lib/status'
import type { TrackerStatus, TrackerTicket } from '@/types/tracker'

const TRACKER_STATUSES: Array<TrackerStatus | 'all'> = [
  'all',
  'new',
  'needs_info',
  'accepted',
  'proposed_closed',
  'closed',
  'on_hold',
  'wont_do',
]

function shortId(id: string) {
  return `#${id.slice(-6).toUpperCase()}`
}

function TrackerRow({
  ticket,
  asUser,
  companyName,
}: {
  ticket: TrackerTicket
  asUser?: string
  /** Present only for the admin cross-company view — renders a company column. */
  companyName?: string
}) {
  const { tracker } = ticket
  // Staff-only, like TrackerControls' "Track this issue" — checked here so
  // the affordance is verifiably absent for a client regardless of who ends
  // up rendering this row. Clients don't present.
  const isAdmin = useIsAdmin()

  return (
    <div className="group flex items-center gap-1 px-3 py-2.5 text-sm transition-colors hover:bg-muted">
      <Link
        // The detail route arrives in a later task — link to the path now so
        // rows are already wired up once it exists.
        to={`/tracker/${ticket._id}` as never}
        search={{ asUser } as never}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        <span className="w-16 shrink-0 text-xs text-muted-foreground tabular-nums">{shortId(ticket._id)}</span>
        {companyName !== undefined && (
          <span className="w-32 shrink-0 truncate text-xs text-muted-foreground">{companyName}</span>
        )}
        <div className="flex-1 min-w-0">
          <span className="block truncate text-foreground">{ticket.subject}</span>
          {tracker.category && (
            <span className="block truncate text-xs text-muted-foreground">{tracker.category}</span>
          )}
        </div>
        <Badge variant={trackerStatusVariants[tracker.status]}>{trackerStatusLabels[tracker.status]}</Badge>
        <Badge variant={devStageVariants[tracker.devStage]}>{devStageLabels[tracker.devStage]}</Badge>
        <span className="w-14 shrink-0 text-right text-xs text-muted-foreground">{priorityLabels[ticket.priority]}</span>
        <span className="w-24 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
          {tracker.expectedDate ? dayjs(tracker.expectedDate.slice(0, 10)).format('D MMM YYYY') : ''}
        </span>
      </Link>
      {isAdmin && (
        // The list mixes every company's tickets for an unlensed admin (see
        // buildTrackerScope) — this row's own companyId is the only reliable
        // way to know which company's deck to open, lensed or not.
        <Link
          to="/tracker/present"
          search={{ companyId: ticket.companyId, asUser } as never}
          aria-label="Present"
          title="Present"
          className="shrink-0 rounded p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100"
        >
          <Presentation className="h-4 w-4" />
        </Link>
      )}
    </div>
  )
}

export function TrackerListView({ asUser, admin = false }: { asUser?: string; admin?: boolean }) {
  const [status, setStatus] = useState<TrackerStatus | 'all'>('all')
  const [companyId, setCompanyId] = useState('')

  const params: Record<string, unknown> = {}
  if (status !== 'all') params.status = status
  if (admin) {
    // companyId is honoured server-side for an admin (supportTicket.service's
    // listTrackerTickets) — sent as a real query param, not filtered
    // client-side, so it narrows the SAME 25-row default page rather than
    // slicing an already-truncated one. Also request the server's max page:
    // an unlensed admin's list spans every company combined, so it can
    // exceed the default page long before any single company's filter would.
    params.limit = 100
    if (companyId) params.companyId = companyId
  }

  const { data, isLoading, error, refetch } = useTrackerTickets(params, asUser)
  // Rows carry only companyId (see listTrackerTickets) — no companyName join
  // server-side — so the company column's display name still comes from the
  // same company list the admin ticket queue's filter uses.
  const companies = useTicketCompanies(admin)
  const companyNames = new Map((companies.data?.data ?? []).map(c => [c._id, c.name ?? c._id]))

  const rows = data?.data ?? []
  // True once the server-side page itself is short of the real total — can't
  // happen for a non-admin (their own scope never approaches 100 promoted
  // tickets) but is exactly the case a bare company-name join over a partial
  // page would otherwise hide silently.
  const truncated = admin && (data?.total ?? 0) > rows.length

  return (
    <>
      {data?.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}
      <div className="flex gap-8">
        <div className="flex-1 min-w-0">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-base font-semibold text-foreground">Tracker</h1>
            {admin && (
              <select
                aria-label="Company"
                className="h-8 rounded border border-input bg-background px-2 text-xs max-w-44"
                value={companyId}
                onChange={e => setCompanyId(e.target.value)}
              >
                <option value="">All companies</option>
                {companies.data?.data.map(c => (
                  <option key={c._id} value={c._id}>{c.name ?? c._id}</option>
                ))}
              </select>
            )}
          </div>

          <div className="mb-4 flex flex-wrap gap-1.5">
            {TRACKER_STATUSES.map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  status === s
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {s === 'all' ? 'All' : trackerStatusLabels[s]}
              </button>
            ))}
          </div>

          {isLoading && <ListSkeleton rows={5} />}

          {error && (
            <div className="flex flex-col items-center gap-3 py-16">
              <EmptyState
                icon={<AlertTriangle />}
                title="Couldn't load the tracker."
                body="Something went wrong. Please try again."
              />
              <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
          )}

          {!isLoading && !error && rows.length === 0 && (
            <EmptyState
              icon={<Milestone />}
              title="No promoted tickets yet"
              body="When we promote a ticket to the tracker it'll show up here."
            />
          )}

          {!isLoading && !error && rows.length > 0 && (
            <>
              {truncated && (
                <p className="mb-2 text-xs text-muted-foreground">
                  Showing first {rows.length} of {data!.total} — narrow with the company filter or a status to see the rest.
                </p>
              )}
              <div className="divide-y divide-border rounded border border-border bg-background">
                {rows.map((t: TrackerTicket) => (
                  <TrackerRow
                    key={t._id}
                    ticket={t}
                    asUser={asUser}
                    companyName={admin ? companyNames.get(t.companyId) ?? 'Unknown company' : undefined}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
